
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Music } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { extractSpotifyTrackId } from '@/lib/spotify';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AddTrackFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const AddTrackForm: React.FC<AddTrackFormProps> = ({ onSuccess, onCancel }) => {
  const [trackUrl, setTrackUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Extract Spotify track ID for preview
  const spotifyTrackId = extractSpotifyTrackId(trackUrl);
  
  // Reset error when URL changes
  useEffect(() => {
    setError(null);
  }, [trackUrl]);
  
  const handleAddTrack = async () => {
    if (!trackUrl) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First, check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('You must be logged in to add tracks. Your session may have expired.');
        setIsLoading(false);
        toast({
          title: 'Authentication Error',
          description: 'Please log in again to continue.',
          variant: 'destructive',
        });
        return;
      }

      // Call the edge function with authentication header
      const { data, error: functionError } = await supabase.functions.invoke('featured-tracks', {
        body: { 
          action: 'add',
          trackUrl
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (functionError) {
        console.error('Error invoking featured-tracks function:', functionError);
        setError(`Error: ${functionError.message || 'Failed to add track'}`);
        toast({
          title: 'Error',
          description: functionError.message || 'Failed to add track',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }
      
      if (data?.error) {
        console.error('Error from featured-tracks function:', data.error);
        setError(`Error: ${data.error}`);
        toast({
          title: 'Error',
          description: data.error || 'Failed to add track',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }
      
      if (data && data.success) {
        toast({
          title: 'Track added',
          description: 'Featured track has been added successfully',
        });
        setTrackUrl('');
        onSuccess();
      } else {
        setError('Unknown error occurred. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to add track',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error adding track:', error);
      setError(`Error: ${error.message || 'Unknown error'}`);
      toast({
        title: 'Error',
        description: 'Failed to add track',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleIframeLoad = () => {
    setPreviewLoading(false);
  };
  
  // Show preview when URL is entered
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setTrackUrl(url);
    
    if (extractSpotifyTrackId(url)) {
      setPreviewLoading(true);
    }
  };

  return (
    <div className="space-y-4 mb-6 border p-4 rounded-md bg-card/50">
      <h3 className="text-lg font-medium">Add Featured Track</h3>
      
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="flex flex-col space-y-4">
        <div>
          <label htmlFor="track-url" className="text-sm font-medium mb-1 block">
            Spotify Track URL
          </label>
          <Input
            id="track-url"
            type="text"
            placeholder="https://open.spotify.com/track/..."
            value={trackUrl}
            onChange={handleUrlChange}
            className="mb-2"
          />
          <p className="text-xs text-muted-foreground">
            Paste a Spotify track URL (e.g., https://open.spotify.com/track/...)
          </p>
        </div>
        
        {/* Preview of the track */}
        {spotifyTrackId && (
          <div className="mt-4 border rounded-md overflow-hidden">
            {previewLoading && (
              <div className="w-full h-[80px] bg-secondary/50 flex items-center justify-center">
                <Skeleton className="w-full h-full absolute" />
              </div>
            )}
            <iframe 
              src={`https://open.spotify.com/embed/track/${spotifyTrackId}?utm_source=generator`}
              width="100%" 
              height="80" 
              frameBorder="0" 
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
              loading="lazy"
              className={`w-full ${previewLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleIframeLoad}
              style={{ transition: "opacity 0.3s ease-in-out" }}
            />
          </div>
        )}
        
        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddTrack}
            disabled={!trackUrl.trim() || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              'Add Track'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTrackForm;
